import React, { useState } from 'react';
import { Mail, FileText, CheckCircle, XCircle, Loader } from 'lucide-react';

export default function EmailPDFGenerator() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    pdf_content: 'This is sample content for the PDF document.'
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  // Get your form ID from https://formspree.io (free - 50 submissions/month)
  const FORMSPREE_ID = 'YOUR_FORM_ID_HERE';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Generate PDF using canvas
  const generatePDF = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 595;
    canvas.height = 842;
    const ctx = canvas.getContext('2d');

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Add border
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3;
    ctx.strokeRect(20, 20, canvas.width - 40, canvas.height - 40);

    // Title
    ctx.fillStyle = '#1e40af';
    ctx.font = 'bold 24px Arial';
    ctx.fillText('Generated PDF Document', 50, 80);

    // Line under title
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(50, 90);
    ctx.lineTo(545, 90);
    ctx.stroke();

    // Content
    ctx.fillStyle = '#374151';
    ctx.font = '16px Arial';
    
    const lines = [
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      '',
      'Message:',
      ...wrapText(ctx, formData.pdf_content, 495)
    ];

    let yPos = 130;
    lines.forEach(line => {
      ctx.fillText(line, 50, yPos);
      yPos += 25;
    });

    // Footer
    ctx.font = '12px Arial';
    ctx.fillStyle = '#6b7280';
    ctx.fillText(`Generated on: ${new Date().toLocaleString()}`, 50, canvas.height - 50);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  };

  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      const testLine = currentLine + word + ' ';
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && currentLine !== '') {
        lines.push(currentLine.trim());
        currentLine = word + ' ';
      } else {
        currentLine = testLine;
      }
    });
    
    if (currentLine) {
      lines.push(currentLine.trim());
    }

    return lines;
  };

  const sendEmail = async () => {
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill all fields');
      return;
    }

    if (FORMSPREE_ID === 'YOUR_FORM_ID_HERE') {
      alert('Please add your Formspree Form ID! Get it free at formspree.io');
      return;
    }

    setLoading(true);
    setStatus('');

    try {
      // Generate PDF
      const pdfBlob = await generatePDF();
      
      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('file', pdfBlob, 'document.png');

      // Send to Formspree
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        console.log('Success!');
        setStatus('success');
        
        // Reset form
        setFormData({ 
          name: '', 
          email: '', 
          message: '',
          pdf_content: 'This is sample content for the PDF document.'
        });
      } else {
        const data = await response.json();
        console.error('Error:', data);
        setStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  const previewPDF = async () => {
    const pdfBlob = await generatePDF();
    const url = URL.createObjectURL(pdfBlob);
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-indigo-100 p-3 rounded-full">
            <FileText className="w-8 h-8 text-indigo-600" />
          </div>
        </div>
        
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
          Generate & Send PDF
        </h1>
        <p className="text-center text-gray-600 mb-8">
          PDF will be auto-generated and sent via Formspree
        </p>

        {/* Setup Instructions */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-green-800 mb-2">🚀 Setup (Free - 50 emails/month):</h3>
          <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
            <li>Go to <strong>formspree.io</strong></li>
            <li>Sign up free (Google/GitHub)</li>
            <li>Create new form → Copy Form ID</li>
            <li>Replace FORMSPREE_ID in code (line 17)</li>
          </ol>
          <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-800">
            ✅ 50 submissions/month • ✅ File attachments included • ✅ No credit card
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="John Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Enter your message..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PDF Content
            </label>
            <textarea
              name="pdf_content"
              value={formData.pdf_content}
              onChange={handleInputChange}
              rows="4"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Content that will appear in the generated PDF..."
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={previewPDF}
              className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors flex items-center justify-center"
            >
              <FileText className="w-5 h-5 mr-2" />
              Preview
            </button>

            <button
              onClick={sendEmail}
              disabled={loading}
              className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5 mr-2" />
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>

        {/* Status Messages */}
        {status === 'success' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" />
            <span className="text-green-800">Email sent successfully with PDF attachment! ✓</span>
          </div>
        )}

        {status === 'error' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <XCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
            <span className="text-red-800">Failed to send. Check console & Form ID.</span>
          </div>
        )}

        {/* Alternative Info */}
        <div className="mt-6 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-600">
            💡 <strong>Need unlimited emails?</strong> Consider using a backend with SendGrid (100/day free) or Resend (3000/month free)
          </p>
        </div>
      </div>
    </div>
  );
}